import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import ScoreBadge from "../../components/ScoreBadge/ScoreBadge";
import { scoreTint } from "../../utils/helpers/scoreColors";
import { num } from "../../utils/helpers/scoring";

// The heatmap's top level when the scope spans more than one school: the same
// grid shape as TeacherDirectory with schools in the teachers' place, fed by
// /analytics/school-matrix/. Clicking a row re-scopes the page to that school,
// which is where the teacher grid comes back.
//
// `matrix` is the SchoolMatrixSerializer payload:
//   { scope: { school_count },
//     standards: [{uuid, code, title, number, order}],
//     schools: [{ organization, avg_percentage, observation_count,
//                 teacher_count, uncovered_count,
//                 per_standard: {std_uuid: pct} }],
//     network: { avg_percentage, per_standard } }

const pct = (v) => {
  const n = num(v);
  return n == null ? null : Math.round(n);
};

// One identity for the no-columns case, so the memos below keep their cache
// through a parent re-render instead of a fresh `[]` busting them.
const NO_STANDARDS = [];

export default function SchoolMatrix({ matrix, onSchoolClick }) {
  const { t } = useTranslation();
  // Empty when the scope spans >1 rubric: the columns are version-specific, so
  // the backend drops them and leaves the rubric-agnostic score and coverage.
  const standards = matrix?.standards || NO_STANDARDS;

  // Cell values are derived once here rather than in the row renderer, the way
  // TeacherDirectory does it, so the grid does one pass over schools × standards.
  const rows = useMemo(
    () =>
      (matrix?.schools || []).map((row) => {
        const org = row.organization || {};
        return {
          uuid: org.uuid,
          name: org.name,
          overall: pct(row.avg_percentage),
          teacherCount: row.teacher_count ?? 0,
          uncovered: row.uncovered_count ?? 0,
          cells: standards.map((std) => {
            const value = pct(row.per_standard?.[std.uuid]);
            return {
              uuid: std.uuid,
              value,
              background: scoreTint(value),
              title: `${org.name} — S${std.order}: ${
                value == null ? t("heatmap.notEvaluated") : `${value}%`
              }`,
            };
          }),
        };
      }),
    [matrix, standards, t],
  );

  // One vote per teacher across the whole scope — a teacher observed at two
  // schools counts once here and in both their rows, so this is not the mean
  // of the rows above it.
  const networkCells = useMemo(
    () =>
      standards.map((std) => {
        const value = pct(matrix?.network?.per_standard?.[std.uuid]);
        return { uuid: std.uuid, value, background: scoreTint(value) };
      }),
    [matrix, standards],
  );

  // The backend's own count of the scope, so the badge, the page's network/school
  // gate and the API can't disagree about how many schools this is. The rows are
  // the whole subtree, head office included: it has its own staff, and a deeper
  // tree puts regions among them, neither of which is a school.
  const schoolCount = matrix?.scope?.school_count ?? 0;

  const gridColsClass = `repeat(${standards.length}, 3.5rem)`;

  return (
    <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden flex flex-col w-fit mx-auto">
      <div className="px-6 py-4 border-b border-slate-100 bg-white flex items-center justify-between gap-4">
        <p className="text-xs text-slate-500">{t("heatmap.matrixHint")}</p>
        <div className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-[10px] font-bold uppercase tracking-wider whitespace-nowrap">
          {t("heatmap.schools", { count: schoolCount })}
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="inline-block align-middle">
          <div className="flex bg-slate-50/50 border-b border-slate-200 sticky top-0 z-20 backdrop-blur-sm text-xs uppercase tracking-widest font-bold text-slate-500">
            <div className="w-64 shrink-0 px-6 py-4 text-left sticky left-0 bg-white/90 z-10 flex items-center">
              {t("heatmap.colSchool")}
            </div>
            <div className="w-20 shrink-0 px-1 py-4 text-center text-[10px] flex items-center justify-center">
              {t("heatmap.colOverall")}
            </div>
            {/* A count, not a name: the teacher grid's "Teacher" column header
                does not fit here. */}
            <div className="w-20 shrink-0 px-1 py-4 text-center text-[10px] flex items-center justify-center">
              {t("heatmap.colTeacherCount")}
            </div>
            <div
              className="w-24 shrink-0 px-1 py-4 text-center text-[10px] flex items-center justify-center border-r border-slate-200"
              title={t("heatmap.uncoveredTip")}
            >
              {t("heatmap.colUncovered")}
            </div>
            <div
              className="grid gap-px bg-slate-100"
              style={{ gridTemplateColumns: gridColsClass }}
            >
              {standards.map((std) => (
                <div
                  key={std.uuid}
                  className="bg-slate-50/50 flex items-center justify-center py-4"
                  title={std.title}
                >
                  {/* Labelled by `order`, which every standard has — `number` is
                      optional; the two agree in every rubric here. */}
                  <span className="font-mono text-xs">S{std.order}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex border-b-2 border-slate-200 bg-slate-50 h-14 items-stretch font-bold">
              <div className="w-64 shrink-0 px-6 text-sm text-slate-800 truncate sticky left-0 bg-slate-50 z-10 flex items-center">
                {t("heatmap.networkRow")}
              </div>
              <div className="w-20 shrink-0 px-4 flex items-center justify-center">
                <ScoreBadge value={pct(matrix?.network?.avg_percentage)} />
              </div>
              <div className="w-20 shrink-0 px-4 flex items-center justify-center text-xs text-slate-400">
                —
              </div>
              <div className="w-24 shrink-0 px-4 flex items-center justify-center text-xs text-slate-400 border-r border-slate-200">
                —
              </div>
              <div
                className="grid gap-px bg-slate-200/60"
                style={{ gridTemplateColumns: gridColsClass }}
              >
                {networkCells.map((cell) => (
                  <div
                    key={cell.uuid}
                    className="flex items-center justify-center text-[10px] font-black text-slate-600"
                    style={{ backgroundColor: cell.background }}
                  >
                    {cell.value == null ? "—" : `${cell.value}%`}
                  </div>
                ))}
              </div>
            </div>

            {rows.length === 0 && (
              // The columns are there — what is missing is schools, not a rubric.
              <div className="px-6 py-10 text-center text-sm text-slate-400">
                {t("heatmap.noSchools")}
              </div>
            )}
            {rows.map((row) => (
              <div
                key={row.uuid}
                className="flex border-b border-slate-50 last:border-b-0 group h-14 items-stretch bg-white"
              >
                <div className="w-64 shrink-0 px-6 text-sm font-medium truncate sticky left-0 bg-white group-hover:bg-slate-50 z-10 flex items-center">
                  <button
                    type="button"
                    onClick={() => onSchoolClick?.(row.uuid)}
                    className="text-left truncate transition-colors text-slate-800 hover:text-blue-600 font-bold text-sm"
                    title={t("heatmap.drillIn", { name: row.name })}
                  >
                    {row.name}
                  </button>
                </div>
                <div className="w-20 shrink-0 px-4 flex items-center justify-center border-r border-slate-100 bg-slate-50/30">
                  <ScoreBadge value={row.overall} />
                </div>
                <div className="w-20 shrink-0 px-4 flex items-center justify-center border-r border-slate-100 bg-slate-50/30">
                  <span className="text-xs font-bold text-slate-600">
                    {row.teacherCount}
                  </span>
                </div>
                <div className="w-24 shrink-0 px-4 flex items-center justify-center border-r border-slate-200 bg-slate-50/30">
                  <span
                    className={`text-xs font-bold ${
                      row.uncovered > 0 ? "text-rose-600" : "text-slate-400"
                    }`}
                  >
                    {row.uncovered}
                  </span>
                </div>
                <div
                  className="grid gap-px bg-slate-50"
                  style={{ gridTemplateColumns: gridColsClass }}
                >
                  {row.cells.map((cell) => (
                    <div
                      key={cell.uuid}
                      className="flex items-center justify-center text-[10px] font-black text-slate-600"
                      style={{ backgroundColor: cell.background }}
                      title={cell.title}
                    >
                      {cell.value == null ? "—" : `${cell.value}%`}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
